"use client";

import { useState, useEffect } from "react";
import { FaCommentDots } from "react-icons/fa6";

// Import children components to render.
import MessagingWindow from "./components/messagingWindow";

import './bootstrapMessaging.css';

import { storeOrganizationId, storeDeploymentDeveloperName, storeSalesforceMessagingUrl } from './services/dataProvider';
import { determineStorageType, initializeWebStorage, getItemInWebStorageByKey, getItemInPayloadByKey } from './helpers/webstorageUtils';
import { APP_CONSTANTS, STORAGE_KEYS } from './helpers/constants';
import { track, AwacEvents } from './awac/analytics';
import { t, useAwacLang } from './awac/i18n';


// ── Embedded Service deployment parameters (AI Real Estate — Pilot Adaptive Agent) ──
// The client auto-initializes with these on load; there is no visible
// deployment form anymore. Edit here to change them.
const DEFAULT_ORG_ID = '00DHs000003XQDL';
const DEFAULT_DEPLOYMENT_DEV_NAME = 'Pilot_Adaptative_Agent_Test';
const DEFAULT_MESSAGING_URL = 'https://airealestate.my.salesforce-scrt.com';

export default function BootstrapMessaging() {
    let [shouldShowMessagingButton, setShowMessagingButton] = useState(false);
    let [shouldDisableMessagingButton, setShouldDisableMessagingButton] = useState(false);
    let [shouldShowMessagingWindow, setShouldShowMessagingWindow] = useState(false);
    let [showMessagingButtonSpinner, setShowMessagingButtonSpinner] = useState(false);
    let [isExistingConversation, setIsExistingConversation] = useState(false);
    // AWAC: minimize state. When true, the MessagingWindow is hidden via CSS
    // (NOT unmounted, so the conversation + SSE connection survive) and a
    // floating launcher bubble is shown to re-expand it.
    let [isMinimized, setIsMinimized] = useState(false);
    // Re-render launcher labels when the page language toggles (FR default).
    useAwacLang();

    useEffect(() => {
        const storage = determineStorageType();
        if (!storage) {
            console.error(`Cannot initialize the app. Web storage is required for the app to function.`);
            return;
        }

        const messaging_webstorage_key = Object.keys(storage).filter(item => item.startsWith(APP_CONSTANTS.WEB_STORAGE_KEY))[0];

        if (messaging_webstorage_key) {
            const webStoragePayload = storage.getItem(messaging_webstorage_key);
            const orgId = getItemInPayloadByKey(webStoragePayload, STORAGE_KEYS.ORGANIZATION_ID);
            const deploymentDevName = getItemInPayloadByKey(webStoragePayload, STORAGE_KEYS.DEPLOYMENT_DEVELOPER_NAME);
            const messagingUrl = getItemInPayloadByKey(webStoragePayload, STORAGE_KEYS.MESSAGING_URL);

            if (!isValidOrganizationId(orgId)) {
                console.warn(`Invalid organization id exists in the web storage: ${orgId}. Cleaning up the invalid object from the web storage.`);
                storage.removeItem(messaging_webstorage_key);
                // New conversation.
                startFreshClient();
                return;
            }

            // Re-initialize the client from the values in the web storage so a
            // messaging session continuation (e.g. page reload) resumes cleanly.
            initializeMessagingClient(orgId, deploymentDevName, messagingUrl);

            const messagingJwt = getItemInWebStorageByKey(STORAGE_KEYS.JWT);
            if (messagingJwt) {
                // Existing conversation — restore the open chat window.
                setIsExistingConversation(true);
                setShowMessagingButton(true);
                setShouldDisableMessagingButton(true);
                setShouldShowMessagingWindow(true);
            } else {
                // New conversation.
                setIsExistingConversation(false);
                setShowMessagingButton(true);
            }
        } else {
            // New conversation — auto-initialize with the deployment defaults.
            startFreshClient();
        }

        return () => {
            showMessagingWindow(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * AWAC: the landing page (hero CTAs, chips, plan rows) asks the chat to
     * open by dispatching a `awac:open-chat` CustomEvent on `window`. This
     * keeps the page markup fully decoupled from the messaging internals.
     */
    useEffect(() => {
        function handleOpenChatEvent(event) {
            const source = (event && event.detail && event.detail.source) || "page";
            track(AwacEvents.CHAT_OPENED, { source, alreadyOpen: Boolean(shouldShowMessagingWindow) });
            setIsMinimized(false);
            if (!shouldShowMessagingWindow) {
                // Re-initialize right before mounting the window: a previously
                // closed conversation clears web storage + in-memory data on
                // unmount, so init at open-time guarantees fresh parameters.
                initializeMessagingClient(DEFAULT_ORG_ID, DEFAULT_DEPLOYMENT_DEV_NAME, DEFAULT_MESSAGING_URL);
                setIsExistingConversation(false);
                setShowMessagingButtonSpinner(true);
                showMessagingWindow(true);
            }
        }
        window.addEventListener('awac:open-chat', handleOpenChatEvent);
        return () => window.removeEventListener('awac:open-chat', handleOpenChatEvent);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShowMessagingWindow]);

    /**
     * Initialize the messaging client with the hardcoded deployment defaults
     * and show the floating chat launcher. Used on first load and again after
     * the user closes a conversation (so the next click starts a fresh one).
     */
    function startFreshClient() {
        initializeMessagingClient(DEFAULT_ORG_ID, DEFAULT_DEPLOYMENT_DEV_NAME, DEFAULT_MESSAGING_URL);
        setIsExistingConversation(false);
        setShowMessagingButton(true);
    }

    /**
     * Initialize the messaging client by
     * 1. internally initializing the Embedded Service deployment paramaters in-memory.
     * 2. initializing Salesforce Organization Id in the browser web storage.
     */
    function initializeMessagingClient(ord_id, deployment_dev_name, messaging_url) {
        // Initialize helpers.
        initializeWebStorage(ord_id || DEFAULT_ORG_ID);
        storeOrganizationId(ord_id || DEFAULT_ORG_ID);
        storeDeploymentDeveloperName(deployment_dev_name || DEFAULT_DEPLOYMENT_DEV_NAME);
        storeSalesforceMessagingUrl(messaging_url || DEFAULT_MESSAGING_URL);
    }

    /**
     * Validates whether the supplied string is a valid Salesforce Organization Id.
     * @returns {boolean}
     */
    function isValidOrganizationId(id) {
        return typeof id === "string" && (id.length === 18 || id.length === 15) && id.substring(0, 3) === APP_CONSTANTS.ORGANIZATION_ID_PREFIX;
    }

    /**
     * Handle a click action from the chat launcher.
     * @param {object} evt - button click event
     */
    function handleMessagingButtonClick(evt) {
        if (evt) {
            console.log("Messaging launcher clicked.");
            track(AwacEvents.CHAT_OPENED, { source: "launcher" });
            // Re-initialize right before mounting the window: a previously
            // closed conversation clears web storage + in-memory data on
            // unmount, so init at open-time guarantees fresh parameters.
            initializeMessagingClient(DEFAULT_ORG_ID, DEFAULT_DEPLOYMENT_DEV_NAME, DEFAULT_MESSAGING_URL);
            setIsExistingConversation(false);
            setShowMessagingButtonSpinner(true);
            showMessagingWindow(true);
        }
    }

    /**
     * Determines whether to render the Messaging Window based on the supplied parameter.
     * @param {boolean} shouldShow - TRUE - render the Messaging WINDOW and FALSE - Do not render the Messaging Window
     */
    function showMessagingWindow(shouldShow) {
        setShouldShowMessagingWindow(Boolean(shouldShow));
        if (!shouldShow) {
            // Reset transient UI state.
            setShouldDisableMessagingButton(false);
            setShowMessagingButtonSpinner(false);
            setIsMinimized(false);
            // Re-initialize the client with the deployment defaults so the
            // launcher can immediately start a fresh conversation.
            startFreshClient();
        }
    }

    /**
     * AWAC: Minimize the Messaging Window. This intentionally does NOT unmount
     * the MessagingWindow component — it only flips a flag that hides it via CSS
     * and shows the floating launcher bubble. Unmounting would tear down the
     * EventSource/SSE connection and lose the in-progress conversation, so we
     * keep the component mounted and just hide it.
     */
    function minimizeMessagingWindow() {
        console.log("Messaging Window minimized.");
        setIsMinimized(true);
    }

    /**
     * AWAC: Re-expand the Messaging Window from the minimized launcher bubble.
     */
    function maximizeMessagingWindow() {
        console.log("Messaging Window maximized.");
        setIsMinimized(false);
    }

    /**
     * Handles the app UI readiness i.e. launcher updates based on whether the Messaging Window UI is rendered.
     * @param {boolean} isReady - TRUE - disable the launcher & remove the spinner and FALSE - otherwise.
     */
    function appUiReady(isReady) {
        // Disable the launcher if the app is UI ready.
        setShouldDisableMessagingButton(isReady);
        // Remove the spinner on the launcher if the app is UI ready.
        setShowMessagingButtonSpinner(!isReady);
    }

    return (
        <>
            {shouldShowMessagingButton && !shouldShowMessagingWindow &&
                <button
                    className="awacChatLauncher"
                    onClick={handleMessagingButtonClick}
                    disabled={shouldDisableMessagingButton}
                    aria-label={t('nav.chat')}>
                    <FaCommentDots aria-hidden="true" />
                    <span>{t('nav.chat')}</span>
                    {showMessagingButtonSpinner && <span className="awacLauncherSpinner" aria-hidden="true"></span>}
                </button>
            }
            {shouldShowMessagingWindow &&
                <MessagingWindow
                    isExistingConversation={isExistingConversation}
                    showMessagingWindow={showMessagingWindow}
                    minimizeMessagingWindow={minimizeMessagingWindow}
                    isMinimized={isMinimized}
                    deactivateMessagingButton={appUiReady} />
            }
            {shouldShowMessagingWindow && isMinimized &&
                <button
                    className="awacLauncherBubble"
                    onClick={maximizeMessagingWindow}
                    aria-label="Reopen chat"
                    title="Reopen chat">
                    <FaCommentDots />
                </button>
            }
        </>
    );
}
